import { ActionIcon, Box, Group, Image, Modal, ScrollArea, Text, Tooltip, UnstyledButton } from '@mantine/core';
import { IconChevronLeft, IconChevronRight, IconDownload, IconX } from '@tabler/icons-react';
import { useEffect, useRef, useState } from 'react';

export interface GalleryPhoto {
  id: string;
  /** adres pliku (pełny rozmiar) */
  src: string;
  /** podpis: skąd zdjęcie (praca, autor, czas, czynność) */
  caption: string;
  /** dodatkowa linia podpisu (np. czynność z listy) */
  detail?: string | null;
  /** nazwa pliku przy pobieraniu */
  fileName?: string;
}

/**
 * Galeria zdjęć na pełnym ekranie: kliknięcie miniatury otwiera ją na tym zdjęciu, dalej strzałki (także ← → na klawiaturze,
 * przesunięcie palcem na telefonie), pasek miniatur, licznik, podpis ze źródłem i pobranie. Esc zamyka.
 */
export function PhotoGallery({ photos, index, onIndex, onClose }: { photos: GalleryPhoto[]; index: number | null; onIndex: (i: number) => void; onClose: () => void }) {
  const open = index !== null && photos.length > 0;
  const i = open ? Math.min(Math.max(index, 0), photos.length - 1) : 0;
  const p = photos[i];
  const go = (d: number) => open && onIndex(Math.min(Math.max(i + d, 0), photos.length - 1));
  const touch = useRef<number | null>(null);
  const strip = useRef<HTMLDivElement>(null);
  const [loaded, setLoaded] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    const key = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight') go(1);
      else if (e.key === 'ArrowLeft') go(-1);
      else if (e.key === 'Home') onIndex(0);
      else if (e.key === 'End') onIndex(photos.length - 1);
    };
    window.addEventListener('keydown', key);
    return () => window.removeEventListener('keydown', key);
  });
  // bieżąca miniatura widoczna w pasku
  useEffect(() => {
    strip.current?.querySelector<HTMLElement>(`[data-thumb="${i}"]`)?.scrollIntoView({ block: 'nearest', inline: 'center', behavior: 'smooth' });
  }, [i, open]);
  // kolejne zdjęcie wczytuje się w tle (szybsze przejście)
  useEffect(() => {
    if (!open) return;
    for (const n of [photos[i + 1], photos[i - 1]]) if (n) new window.Image().src = n.src;
  }, [i, open, photos]);

  if (!open || !p) return null;
  return (
    <Modal
      opened
      onClose={onClose}
      fullScreen
      withCloseButton={false}
      padding={0}
      transitionProps={{ transition: 'fade', duration: 120 }}
      styles={{ content: { background: '#0b0d10', display: 'flex', flexDirection: 'column' }, body: { flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, padding: 0 } }}
    >
      <Group justify="space-between" px="md" py="xs" wrap="nowrap" style={{ color: '#fff' }}>
        <Text size="sm" c="gray.4" aria-live="polite">
          {i + 1} / {photos.length}
        </Text>
        <Group gap={4} wrap="nowrap">
          <Tooltip label="Pobierz">
            <ActionIcon component="a" href={p.src} download={p.fileName ?? true} variant="subtle" color="gray.3" size="lg" aria-label="Pobierz zdjęcie">
              <IconDownload size={20} />
            </ActionIcon>
          </Tooltip>
          <Tooltip label="Zamknij (Esc)">
            <ActionIcon onClick={onClose} variant="subtle" color="gray.3" size="lg" aria-label="Zamknij galerię" data-autofocus>
              <IconX size={22} />
            </ActionIcon>
          </Tooltip>
        </Group>
      </Group>

      <Box
        style={{ flex: 1, minHeight: 0, position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', userSelect: 'none', touchAction: 'pan-y' }}
        onTouchStart={(e) => (touch.current = e.touches[0]?.clientX ?? null)}
        onTouchEnd={(e) => {
          const start = touch.current;
          const end = e.changedTouches[0]?.clientX;
          touch.current = null;
          if (start === null || end === undefined) return;
          if (end - start > 50) go(-1);
          else if (start - end > 50) go(1);
        }}
      >
        <img
          key={p.id}
          src={p.src}
          alt={p.caption}
          onLoad={() => setLoaded(p.id)}
          style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', opacity: loaded === p.id ? 1 : 0.4, transition: 'opacity 120ms' }}
        />
        <ActionIcon
          onClick={() => go(-1)}
          disabled={i === 0}
          variant="filled"
          color="dark"
          radius="xl"
          size={44}
          aria-label="Poprzednie zdjęcie"
          style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', opacity: i === 0 ? 0.25 : 0.85 }}
        >
          <IconChevronLeft size={26} />
        </ActionIcon>
        <ActionIcon
          onClick={() => go(1)}
          disabled={i === photos.length - 1}
          variant="filled"
          color="dark"
          radius="xl"
          size={44}
          aria-label="Następne zdjęcie"
          style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', opacity: i === photos.length - 1 ? 0.25 : 0.85 }}
        >
          <IconChevronRight size={26} />
        </ActionIcon>
      </Box>

      <Box px="md" pt="xs" style={{ color: '#fff' }}>
        <Text size="sm" c="gray.2" ta="center">
          {p.caption}
        </Text>
        {p.detail && (
          <Text size="xs" c="gray.5" ta="center">
            {p.detail}
          </Text>
        )}
      </Box>
      {photos.length > 1 && (
        <ScrollArea type="auto" scrollbarSize={6} py="sm" viewportRef={strip}>
          <Group gap={6} wrap="nowrap" px="md" justify={photos.length < 12 ? 'center' : 'flex-start'}>
            {photos.map((x, n) => (
              <UnstyledButton
                key={x.id}
                data-thumb={n}
                onClick={() => onIndex(n)}
                aria-label={`Zdjęcie ${n + 1}: ${x.caption}`}
                aria-current={n === i}
                style={{ flexShrink: 0, borderRadius: 6, outline: n === i ? '2px solid var(--mantine-color-eko-4)' : '2px solid transparent', outlineOffset: 1, opacity: n === i ? 1 : 0.55 }}
              >
                <Image src={x.src} w={64} h={48} fit="cover" radius={4} loading="lazy" alt="" />
              </UnstyledButton>
            ))}
          </Group>
        </ScrollArea>
      )}
    </Modal>
  );
}

/** Stan galerii dla listy zdjęć: `open(i)` z miniatury, `gallery` do wstawienia w widoku. */
export function useGallery(photos: GalleryPhoto[]) {
  const [index, setIndex] = useState<number | null>(null);
  return {
    open: (id: string) => {
      const n = photos.findIndex((p) => p.id === id);
      if (n >= 0) setIndex(n);
    },
    gallery: <PhotoGallery photos={photos} index={index} onIndex={setIndex} onClose={() => setIndex(null)} />,
  };
}
